import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
    Layout,
    Menu,
    Avatar,
    Dropdown,
    Input,
    Card,
    Descriptions,
    Row,
    Col,
    Spin, Modal
} from 'antd';
import {UserOutlined} from '@ant-design/icons';
import debounce from 'lodash.debounce';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../app/store';
import {useTranslation} from 'react-i18next';
import {logout} from '../app/persistedSlice';
import {useHistory} from 'react-router-dom';
import {PerformanceCardSearchResultDto, useLazyGetApiSearchSearchQuery} from "../api/services/Search";
import moment from "moment";
import {useOnClickOutside} from "usehooks-ts";
import TeamPerformanceCardModal from "../pages/TeamsOKRs/Components/TeamPerformanceCardModal";
import {ActivePerformanceCardDto} from "../api/services/performanceCards";
import Search from "antd/es/input/Search";
import {useLazyGetApiMCompanySettingsByCompanyIdQuery} from "../api/services/company";
import {setError, setLoading} from "../features/app/appSlice";
import {t} from "i18next";

const {Header} = Layout;

interface Person {
    id: string;
    name: string;
    summary: string;
}

const AppHeader: React.FC = () => {
    const {t, i18n} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const ref = useRef(null)

    const user = useSelector((state: RootState) => state?.auth?.user);
    const tenantInfo = useSelector((state: RootState) => state?.persisted?.tenantInfo);
    const themeColors = useSelector((state: RootState) => state?.app?.themeColors);

    // Search state
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<PerformanceCardSearchResultDto[]>([]);
    const [openSearch, setOpenSearch] = useState(false);
    const [loading, setLoading] = useState(false);
    const [search] = useLazyGetApiSearchSearchQuery()
    const [fetchSettings] = useLazyGetApiMCompanySettingsByCompanyIdQuery()
    const [selectedCard, setSelectedCard] = useState<ActivePerformanceCardDto | null>(null);
    const [visible, setVisible] = useState(false);
    const [userSearchEnabled, setUserSearchEnabled] = useState(false)

    useEffect(() => {
        if(user) {
            getSettings('userSearchEnabled')
        }
    }, [user]);
    const getSettings = async (key: string) => {
        try {
            var setting = await fetchSettings({
                companyId: user?.companyId ?? "",
                keys: [key]
            }).unwrap()

            let val = (setting as any[])?.find(k => k.key == key)?.value
            setUserSearchEnabled(val == null ? false : val == "true")
        } catch (err: any) {
            if (err.status != "PARSING_ERROR")
                dispatch(setError(t('Ayar çekilirken hata oluştu')));
        }
    }

    // Actual fetch function
    const doSearch = async (q: string) => {
        if (q.trim().length < 3) {
            setResults([]);
            setLoading(false);
            setOpenSearch(false);
            return;
        }

        setLoading(true);
        try {
            const res = await search({
                keyword: q
            }).unwrap();

            setResults(res);
        } catch {
            setResults([]);
        } finally {
            setLoading(false);
            setOpenSearch(true);
        }
    };

    // Debounced version
    const debouncedSearch = useCallback(
        debounce((q: string) => {
            if (q.trim() !== '') {
                doSearch(q);
            }
        }, 300),
        []
    );

    useEffect(() => {
        if (query.trim() === '') {
            debouncedSearch.cancel();
            setResults([]);
            setOpenSearch(false);
            setLoading(false);
        } else {
            debouncedSearch(query);
        }
    }, [query, debouncedSearch]);

    // Menu handlers
    const logoutClicked = () => {
        dispatch(logout());
        history.push(`/${tenantInfo?.id}/`);
    };
    const profileClicked = () => {
        history.push(`/${tenantInfo?.id}/profile`);
    };

    // Dropdown menu
    const menu = (
        <Menu>
            <Menu.Item key="profile" onClick={profileClicked}>
                {t('profile')}
            </Menu.Item>
            <Menu.Item key="logout" onClick={logoutClicked}>
                {t('logout')}
            </Menu.Item>
            {/*<Menu.SubMenu key="lang" title={t('language')}>*/}
            {/*    <Menu.Item onClick={() => i18n.changeLanguage('en')}>English</Menu.Item>*/}
            {/*    <Menu.Item onClick={() => i18n.changeLanguage('tr')}>Türkçe</Menu.Item>*/}
            {/*</Menu.SubMenu>*/}
        </Menu>
    );

    const handleClickOutside = () => {
        setOpenSearch(false);
    }

    useOnClickOutside(ref, handleClickOutside)

    const handleCancel = () => {
        setVisible(false);
        setSelectedCard(null);
    };

    const showCard = (card) => {
        setSelectedCard({
            cardId: card?.cardDetails?.cardAssignmentId,
            cardName: card?.cardDetails?.cardName,
            endDate: card?.cardDetails?.period?.endDate,
            periodName: card?.cardDetails?.period?.periodName,
            periodId: card?.cardDetails?.period?.id,
            startDate: card?.cardDetails?.period?.startDate
        } as ActivePerformanceCardDto);
        setOpenSearch(false);
        setVisible(true);
    }

    return (
        <>
            <Header
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 24px',
                    background: themeColors?.primaryColor ?? '#fff',
                    backdropFilter: 'blur(10px)',
                    zIndex: 2000,
                }}
            >
                {/* Logo */}
                <div
                    className="logo"
                    style={{display: 'flex', alignItems: 'center', width: 200, height: 60}}
                >
                    {/* Logo yüklenmemiş kurulumlarda (örn. yeni on-premise kurulum)
                        kırık görsel yerine şirket adı gösterilir. */}
                    {tenantInfo?.logo
                        ? <img
                            alt={tenantInfo?.name}
                            src={`${process.env.REACT_APP_API_IMAGE_URL}/api/image/images/companies/${tenantInfo.logo}`}
                            style={{maxWidth: 200, height: 60, objectFit: 'contain'}}
                        />
                        : <span style={{fontSize: 16, fontWeight: 600}}>{tenantInfo?.name}</span>
                    }
                </div>

                {/* Search */}
                <div style={{position: 'relative', flex: 1, margin: '0 85px'}}>
                    {userSearchEnabled &&
                        <Input
                            placeholder={t('Kişi Ara')}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            bordered={false}
                            style={{
                                width: '100%',
                                borderRadius: 6,
                                verticalAlign: 'middle',
                                background: (themeColors?.textColor ?? '#000000') + '20',
                            }}

                            variant={"borderless"}
                            allowClear
                            maxLength={60}
                            onFocus={() => {
                                if (query.trim().length > 0) {
                                    doSearch(query);
                                }
                            }}
                        />
                    }
                    {/* Results panel */}
                    {openSearch && !loading && (
                        <div
                            ref={ref}
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                marginTop: 4,
                                maxHeight: 500,
                                overflowY: 'auto',
                                background: (themeColors?.primaryColor ?? '#000') + 'ff',
                                backdropFilter: 'blur(10px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                borderRadius: 6,
                                zIndex: 9999,
                            }}
                        >
                            {results.length > 0 ? (
                                results.map(p => (
                                    <Card style={{backgroundColor: 'transparent'}} bordered={false}
                                          classNames={{body: "cursor-pointer"}}
                                          onClick={e => showCard(p)}>
                                        <Row>
                                            <Col span={2}>
                                                <Avatar size={80} icon={<UserOutlined/>} style={{marginBottom: 20}}/>
                                            </Col>
                                            <Col span={22}>
                                                <Descriptions column={1}>
                                                    <Descriptions.Item
                                                        style={{paddingBottom: 0}}
                                                        label={t('name-surname')}
                                                        labelStyle={{fontWeight: 'bolder'}}
                                                    >
                                                        {p?.firstName} {p?.lastName}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item
                                                        style={{paddingBottom: 0}}
                                                        label="Ünvan"
                                                        labelStyle={{fontWeight: 'bolder'}}
                                                    >
                                                        {p?.titleName}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item
                                                        style={{paddingBottom: 0}}
                                                        contentStyle={{marginBottom: 0}}
                                                        label="Kart Adı"
                                                        labelStyle={{fontWeight: 'bolder'}}
                                                    >
                                                        {p?.cardDetails?.cardName}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item
                                                        style={{paddingBottom: 0}}
                                                        label="Performans Dönemi"
                                                        labelStyle={{fontWeight: 'bolder'}}
                                                    >
                                                        {p?.cardDetails?.period?.periodName} ({moment(p?.cardDetails?.period?.startDate).format('DD.MM.YYYY')} - {moment(p?.cardDetails?.period?.endDate).format('DD.MM.YYYY')})
                                                    </Descriptions.Item>
                                                </Descriptions>
                                            </Col>
                                        </Row>
                                    </Card>
                                ))
                            ) : (
                                <p style={{
                                    color: themeColors?.textColor ?? '#000000',
                                    fontWeight: 'bolder',
                                    textAlign: "center"
                                }}>Sonuç Bulunamadı</p>
                            )}
                        </div>
                    )}
                </div>

                {/* User dropdown */}
                <Dropdown
                    overlay={menu}
                    placement="bottomCenter"
                    trigger={['click']}
                    arrow
                >
                    <div style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                        <Avatar icon={<UserOutlined/>}/>
                        <span style={{marginLeft: 8, color: themeColors?.textColor ?? '#000000'}}>
            {user?.firstName} {user?.lastName}
          </span>
                    </div>
                </Dropdown>
            </Header>

            <Modal
                title={selectedCard?.cardName ?? ""}
                visible={visible}
                onCancel={handleCancel}
                maskClosable={false}
                footer={null}
                width={1000}
                destroyOnClose={true}
            >
                <TeamPerformanceCardModal
                    readonly={true}
                    card={selectedCard}
                    showPublicOnly={true}/>
            </Modal>
        </>
    );
};

export default AppHeader;
